package com.essntl.features.invoice.ui.addeditinvoice

import com.essntl.config.EssntlConfig
import com.essntl.core.quickbooks.data.*
import com.essntl.core.quickbooks.data.QuickbooksInvoiceRequest.Line.Companion.SALES_ITEM_LINE_DETAIL
import com.essntl.core.quickbooks.domain.QuickbooksClient
import com.essntl.core.stripe.domain.StripeClient
import com.essntl.core.stripe.domain.models.PaymentLink
import com.essntl.core.stripe.domain.models.Price
import core.dateformat.utils.formatDateToYYYYMMDD
import core.dateformat.utils.fromDDTOMM
import core.dateformat.utils.stringToLocalDateTime
import com.essntl.core.utils.error.BadRequestException
import com.essntl.core.utils.utils.Either
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.core.utils.viewmodel.getDataSuspend
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.invoice.domain.model.InvoiceModel
import com.essntl.features.invoice.domain.repository.InvoiceRepository
import com.essntl.features.invoice.ui.addeditinvoice.price.getStripeProducts
import com.essntl.features.invoice.ui.addeditinvoice.utils.onEachSafeAwait
import com.essntl.features.itinerary.domain.model.ItineraryModel
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.paymentlink.domain.model.PaymentLinkModel
import com.essntl.features.paymentlink.domain.repository.PaymentLinkRepository
import com.essntl.features.paymentparameter.domain.repository.PaymentParameterRepository
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.service.domain.model.service.ServiceModel
import com.essntl.features.service.domain.repository.ServiceRepository
import com.essntl.features.travelagent.domain.model.TravelAgentModel
import com.essntl.features.travelagent.domain.repository.TravelAgentRepository
import com.essntl.toastmanager.ToastManager
import core.sentry.captureException
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class AddEditInvoiceViewModel(
    private val invoiceRepository: InvoiceRepository,
    private val categoryRepository: CategoryRepository,
    private val serviceRepository: ServiceRepository,
    private val paymentLinkRepository: PaymentLinkRepository,
    private val proposalRepository: ProposalRepository,
    private val itineraryRepository: ItineraryRepository,
    private val clientRepository: ClientRepository,
    private val travelAgentRepository: TravelAgentRepository,
    private val paymentParameterRepository: PaymentParameterRepository,
    private val quickbooksClient: QuickbooksClient,
    private val stripeClient: StripeClient,
    private val toastManager: ToastManager,
    private val state: MutableStateFlow<AddEditInvoiceState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    init {
        getAllCategory()
        getPaymentParameters()

        scope.launch {
            quickbooksClient.token.collect {
                if (it.isNotEmpty() && state.value.openQuickbooksRefreshWebpage) {
                    closeQuickbooksRefreshWebpage()
                    scope.launch {
                        toastManager.success(description = "Quickbooks token refreshed successfully")
                    }
                }
            }
        }
    }

    fun onEvent(event: AddEditInvoiceEvent) {
        when (event) {
            is AddEditInvoiceEvent.GetInvoice -> getInvoice(event)

            is AddEditInvoiceEvent.GetProposalItinerary -> getProposalItinerary(event)

            is AddEditInvoiceEvent.CheckInitialServices -> checkInitialServices(event)

            is AddEditInvoiceEvent.CheckService -> checkService(event)

            is AddEditInvoiceEvent.CheckDayServices -> checkDayServices(event)

            is AddEditInvoiceEvent.AddExtraService -> addExtraService(event)

            is AddEditInvoiceEvent.RemoveExtraService -> removeExtraService(event)

            is AddEditInvoiceEvent.UpdateDueDateField -> updateDueDateField(event)

            is AddEditInvoiceEvent.ToggleClientTravelAgent -> toggleClientTravelAgent(event)

            is AddEditInvoiceEvent.CloseQuickbooksRefreshWebpage -> closeQuickbooksRefreshWebpage()

            is AddEditInvoiceEvent.DeactivatePaymentLink -> deactivatePaymentLink(event)

            is AddEditInvoiceEvent.AddPaymentLink -> addPaymentLink(event)

            is AddEditInvoiceEvent.SparseUpdateInvoice -> sparseUpdateInvoice(event)

            is AddEditInvoiceEvent.DownloadInvoice -> downloadInvoice(event)

            is AddEditInvoiceEvent.ClearDownloadInvoice -> clearDownloadInvoice()

            is AddEditInvoiceEvent.CreateUpdateInvoice -> createUpdateInvoice()
        }
    }

    private fun getAllCategory() =
        getData(
            getData = { categoryRepository.getAll(includeExtra = true) },
            getState = { state.value.getAllCategory },
            setState = { state.value = state.value.copy(getAllCategory = it) },
        )

    private fun getPaymentParameters() =
        getData(
            getData = {
                paymentParameterRepository
                    .getAll()
                    .map { list ->
                        list.filter { it.isInvoice == false }
                    }
            },
            getState = { state.value.getPaymentParameters },
            setState = { newState -> state.update { it.copy(getPaymentParameters = newState) } },
        )

    private fun getInvoice(event: AddEditInvoiceEvent.GetInvoice) {
        if (event.invoiceId.isNullOrEmpty())
            state.update {
                it.copy(
                    dueDate = Clock.System.now().toLocalDateTime(TimeZone.UTC).date.toString(),
                    extraServiceList = State.success(emptyList()),
                )
            }
        else
            getData(
                getData = { invoiceRepository.getById(event.invoiceId) },
                getState = { state.value.getInvoice },
                setState = { newState -> state.update { it.copy(getInvoice = newState) } },
                onSuccess = {
                    val itineraryId = it.itineraryId

                    it.qbId?.let { qbId ->
                        getQuickBooksInvoice(qbId)
                    }

                    it.paymentLinkIds?.let { ids ->
                        if (ids.isNotEmpty())
                            getInvoicePaymentLinks(ids)
                    }

                    if (!itineraryId.isNullOrEmpty())
                        it.itineraryServiceIds?.let { ids ->
                            if (ids.isNotEmpty())
                                getInvoiceItineraryServices(ids)
                        }
                    else
                        it.proposalServiceIds?.let { ids ->
                            if (ids.isNotEmpty())
                                getInvoiceProposalServices(ids)
                        }

                    it.extraServiceIds?.let { ids ->
                        if (ids.isNotEmpty())
                            getInvoiceExtraServices(ids)
                    }
                },
            )
    }

    private fun getQuickBooksInvoice(id: String) =
        getData(
            getData = { quickbooksClient.getInvoice(id) },
            getState = { state.value.getQuickbooksInvoice },
            setState = { newState -> state.update { it.copy(getQuickbooksInvoice = newState) } },
            onSuccess = { qbInvoice ->
                state.update {
                    it.copy(
                        dueDate = qbInvoice.DueDate,
                    )
                }
            },
        )

    private fun getInvoicePaymentLinks(ids: List<String>) =
        getData(
            getData = { paymentLinkRepository.getAllByIds(ids) },
            getState = { state.value.getPaymentLinks },
            setState = { newState -> state.update { it.copy(getPaymentLinks = newState) } },
        )

    private fun getInvoiceProposalServices(ids: List<String>) =
        getData(
            getData = { proposalRepository.getAllProposalServiceByIds(ids = ids) },
            getState = { state.value.getInvoiceProposalServices },
            setState = { newState -> state.update { it.copy(getInvoiceProposalServices = newState) } },
        )

    private fun getInvoiceItineraryServices(ids: List<String>) =
        getData(
            getData = { itineraryRepository.getItineraryServicesByIds(ids = ids) },
            getState = { state.value.getInvoiceItineraryServices },
            setState = { newState -> state.update { it.copy(getInvoiceItineraryServices = newState) } },
        )

    private fun getInvoiceExtraServices(ids: List<String>) =
        getData(
            getData = { serviceRepository.getExtraServicesByIds(ids = ids) },
            getState = { state.value.extraServiceList },
            setState = { newState -> state.update { it.copy(extraServiceList = newState) } },
        )

    private fun getProposalItinerary(event: AddEditInvoiceEvent.GetProposalItinerary) {
        val itineraryId = event.itineraryId
        if (!itineraryId.isNullOrEmpty())
            getData(
                getData = { itineraryRepository.get(itineraryId) },
                getState = { state.value.getItinerary },
                setState = { newState -> state.update { it.copy(getItinerary = newState) } },
                onSuccess = { itinerary ->
                    getItineraryServices(itinerary)
                },
            )
        else
            getData(
                getData = { proposalRepository.getProposal(event.proposalId) },
                getState = { state.value.getProposal },
                setState = { newState -> state.update { it.copy(getProposal = newState) } },
                onSuccess = { proposal ->
                    getProposalServices(proposal)
                },
            )
    }

    private fun getProposalServices(proposal: ProposalModel) =
        getData(
            getData = {
                proposalRepository.getAllProposalServiceByRange(
                    proposalId = proposal.id,
                    fromDate = proposal.fromDate.orEmpty(),
                    toDate = proposal.toDate.orEmpty(),
                )
            },
            getState = { state.value.getProposalServices },
            setState = { newState -> state.update { it.copy(getProposalServices = newState) } },
        )

    private fun getItineraryServices(itinerary: ItineraryModel) =
        getData(
            getData = {
                itineraryRepository.getAllItineraryService(
                    itineraryId = itinerary.id,
                    excludeStatus = ItineraryServiceModel.Status.Cancelled,
                )
            },
            getState = { state.value.getItineraryServices },
            setState = { newState -> state.update { it.copy(getItineraryServices = newState) } },
        )

    private fun checkInitialServices(event: AddEditInvoiceEvent.CheckInitialServices) {
        if (event.invoiceId.isNullOrEmpty()) {
            val proposalServiceIds = state.value.getAllProposalServices.map { it.id }
            state.update {
                it.copy(checkedServiceIds = proposalServiceIds.toSet())
            }
        } else {
            val invoice = state.value.getInvoice.data ?: return
            val invoiceCreatedAt = stringToLocalDateTime(invoice.createdAt) ?: return

            println("invoiceCreatedAt $invoiceCreatedAt")

            val invoiceUpdatedAt = stringToLocalDateTime(invoice.updatedAt) ?: invoiceCreatedAt

            val allProposalServices = state.value.getAllProposalServices
            val invoiceProposalServiceIds = state.value.getInvoiceProposalServices.data
                .orEmpty()
                .map { it.id }

            val allItineraryServices = state.value.getAllItineraryServices
            val invoiceItineraryServiceIds = state.value.getInvoiceItineraryServices.data
                .orEmpty()
                .map { it.id }

            val filteredProposalServiceIds = allProposalServices
                .filterNot {
                    val serviceDateTime = stringToLocalDateTime(it.createdAt) ?: return
                    if (invoiceCreatedAt == invoiceUpdatedAt)
                        !invoiceProposalServiceIds.contains(it.id) &&
                            invoiceCreatedAt > serviceDateTime
                    else if (serviceDateTime > invoiceUpdatedAt)
                        !invoiceProposalServiceIds.contains(it.id) &&
                            invoiceUpdatedAt > serviceDateTime
                    else
                        !invoiceProposalServiceIds.contains(it.id)
                }.map { it.id }

            val filteredItineraryServiceIds = allItineraryServices
                .filterNot {
                    val serviceDateTime = stringToLocalDateTime(it.createdAt) ?: return
                    if (invoiceCreatedAt == invoiceUpdatedAt)
                        !invoiceItineraryServiceIds.contains(it.id) &&
                            invoiceCreatedAt > serviceDateTime
                    else if (serviceDateTime > invoiceUpdatedAt) {
                        !invoiceItineraryServiceIds.contains(it.id) &&
                            invoiceUpdatedAt > serviceDateTime
                    } else {
                        !invoiceItineraryServiceIds.contains(it.id)
                    }
                }.map { it.id }


            state.update {
                it.copy(
                    checkedServiceIds =
                        if (!invoice.itineraryId.isNullOrEmpty())
                            filteredItineraryServiceIds.toSet() else
                            filteredProposalServiceIds.toSet(),
                )
            }
        }
    }

    private fun checkService(event: AddEditInvoiceEvent.CheckService) {
        val checkedServiceIds =
            if (event.isChecked)
                state.value.checkedServiceIds + event.id
            else
                state.value.checkedServiceIds - event.id

        state.value = state.value.copy(checkedServiceIds = checkedServiceIds)
    }

    private fun checkDayServices(event: AddEditInvoiceEvent.CheckDayServices) {
        val itineraryId = state.value.getItinerary.data?.id

        val serviceIds = if (itineraryId.isNullOrEmpty())
            state.value.getAllProposalServices
                .filter { it.startDate == event.date }
                .map { it.id }
                .toSet()
        else
            state.value.getAllItineraryServices
                .filter { it.startDate == event.date }
                .map { it.id }
                .toSet()

        val checkedServiceIds =
            if (event.isChecked)
                state.value.checkedServiceIds + serviceIds
            else
                state.value.checkedServiceIds - serviceIds

        state.value = state.value.copy(checkedServiceIds = checkedServiceIds)
    }

    private fun addExtraService(event: AddEditInvoiceEvent.AddExtraService) {
        val extraServiceList = state.value.extraServiceList.data.orEmpty() + event.extraService

        state.update {
            it.copy(
                extraServiceList = state.value.extraServiceList.copy(
                    data = extraServiceList.distinctBy { es -> es.id },
                ),
            )
        }
    }

    private fun removeExtraService(event: AddEditInvoiceEvent.RemoveExtraService) {
        val extraServiceList = state.value.extraServiceList.data.orEmpty()
            .filter { it.id != event.id }

        state.update {
            it.copy(
                extraServiceList = state.value.extraServiceList.copy(
                    data = extraServiceList,
                ),
            )
        }
    }

    private fun updateDueDateField(event: AddEditInvoiceEvent.UpdateDueDateField) {
        state.update {
            it.copy(
                dueDate = event.dueDate,
            )
        }
    }

    private fun toggleClientTravelAgent(event: AddEditInvoiceEvent.ToggleClientTravelAgent) {
        state.update {
            it.copy(
                clientInvoice = event.value,
            )
        }
    }

    private fun closeQuickbooksRefreshWebpage() {
        state.update {
            it.copy(
                openQuickbooksRefreshWebpage = false,
            )
        }
    }

    private fun deactivatePaymentLink(event: AddEditInvoiceEvent.DeactivatePaymentLink) {
        val linkId = event.paymentLink.linkId ?: return

        state.update {
            it.copy(
                deactivatePaymentLinkLoadingSet = it.deactivatePaymentLinkLoadingSet + linkId,
            )
        }

        scope.launch {
            val updatedPaymentLink = stripeClient
                .deactivatePaymentLink(
                    paymentLinkId = linkId,
                    active = event.active,
                )
                .getOrNull()
                ?: return@launch

            deactivateOldPaymentLinks(
                newPaymentLink = updatedPaymentLink,
            )

            toastManager.success(description = "Payment link ${if (event.active) "activated" else "deactivated"}")
        }.invokeOnCompletion {
            state.update {
                it.copy(
                    deactivatePaymentLinkLoadingSet = it.deactivatePaymentLinkLoadingSet - linkId,
                )
            }
        }
    }

    private fun addPaymentLink(event: AddEditInvoiceEvent.AddPaymentLink) {
        val quickbooksInvoice = state.value.getQuickbooksInvoice.data ?: return
        val invoice = state.value.getInvoice.data ?: return
        val proposal = state.value.getProposal.data ?: return
        val paymentLinks = state.value.getPaymentLinks.data.orEmpty()
        val paymentLinkCount = if (paymentLinks.isEmpty()) " " else ".${paymentLinks.size + 1} "
        val invoiceTitle =
            quickbooksInvoice.DocNumber + paymentLinkCount + state.value.getProposal.data?.primaryClient?.lastName.orEmpty()
        val clientName = state.value.getProposal.data?.primaryClient?.prettyName.orEmpty()

        // Get applied payment parameters
        val paymentParameters = state.value.getPaymentParameters.data
            .orEmpty()
            .filter {
                it.id in event.paymentParameterIds
            }

        // Apply payment parameters to total amount
        val productPriceList = getStripeProducts(
            invoiceDocNumber = invoiceTitle,
            servicesTotalPrice = event.amount,
            paymentParameterList = paymentParameters,
        )

        val totalAmount = productPriceList.sumOf { (_, price) -> price }

        scope.launch {
            state.update {
                it.copy(
                    addPaymentLink = State.loading(),
                )
            }
            loadingManager.emitLoading(true)

            val stripePriceList = mutableListOf<Price>()
            productPriceList
                .map { (name, price) ->
                    async {
                        // Create stripe product
                        val stripeProduct =
                            stripeClient
                                .createProduct(name = name)
                                .getOrNull()
                                ?: return@async Result.failure(Exception("Failed to create stripe product"))

                        // Create stripe price
                        val stripePrice =
                            stripeClient
                                .createPrice(
                                    currency = "usd",
                                    productId = stripeProduct.id,
                                    unitAmount = price,
                                )

                        stripePrice
                    }
                }
                .onEachSafeAwait {
                    stripePriceList.add(it)
                }

            // Create stripe payment link
            val stripePaymentLink = stripeClient
                .createPaymentLink(
                    priceIds = stripePriceList.map { it.id },
                    quantity = 1,
                    metadata = PaymentLink.Metadata(
                        client = clientName,
                        travelAgent = proposal.travelAgent?.prettyName,
                        product = invoiceTitle,
                        proposal = proposal.title,
                        invoiceId = invoice.id,
                        qbInvoiceId = quickbooksInvoice.Id,
                    ),
                )
                .getOrNull()
                ?: return@launch

            deactivateOldPaymentLinks(
                newPaymentLink = stripePaymentLink,
            )

            // Create payment link in the database
            val paymentLink =
                PaymentLinkModel.empty.copy(
                    linkUrl = stripePaymentLink.url,
                    linkId = stripePaymentLink.id,
                    proposalId = proposal.id,
                    productName = invoiceTitle,
                    amount = totalAmount,
                    hasMerchantFee = false,
                    active = true,
                )

            val createdPaymentLink =
                paymentLinkRepository
                    .insert(paymentLink = paymentLink)
                    .getOrNull()
                    ?: return@launch

            // Add payment link id to invoice
            val newInvoice =
                invoice.copy(
                    paymentLinkIds = invoice.paymentLinkIds.orEmpty() + createdPaymentLink.id,
                )

            invoiceRepository
                .update(newInvoice)
                .onFailure {
                    // Delete the payment link in case of failure
                    paymentLinkRepository
                        .delete(createdPaymentLink.id)
                }
                .getOrNull()
                ?: return@launch

            val paymentLinkList = state.value.getPaymentLinks.data
                .orEmpty()
                .toMutableList()
                .apply { add(0, createdPaymentLink) }
                .toList()

            state.update {
                it.copy(
                    addPaymentLink = State.success(Unit),
                    getPaymentLinks = it.getPaymentLinks.copy(
                        data = paymentLinkList,
                    ),
                )
            }
        }.invokeOnCompletion {
            scope.launch {
                loadingManager.emitLoading(false)
                if (state.value.addPaymentLink.isSuccess)
                    toastManager.success(description = "Invoice created successfully")
                else
                    state.update {
                        it.copy(
                            addPaymentLink = State.failure(),
                        )
                    }
            }
        }
    }

    private suspend fun deactivateOldPaymentLinks(
        newPaymentLink: PaymentLink,
    ) = coroutineScope {
        val updatedPaymentLinks = mutableListOf(newPaymentLink)

        // Deactivate other links
        if (newPaymentLink.active)
            state.value.getPaymentLinks.data
                .orEmpty()
                .filter { it.active == true && it.linkId != null && it.linkId != newPaymentLink.id }
                .map {
                    async {
                        stripeClient.deactivatePaymentLink(
                            paymentLinkId = it.linkId.orEmpty(),
                            active = false,
                        )
                    }
                }
                .awaitAll()
                .mapNotNull { it.getOrNull() }
                .forEach { link ->
                    updatedPaymentLinks.add(link)
                }

        val updatedPaymentLinkModelList =
            updatedPaymentLinks.mapNotNull { link ->
                state.value.getPaymentLinks.data
                    .orEmpty()
                    .find { it.linkId == link.id }
                    ?.copy(
                        active = link.active,
                    )
            }

        updatePaymentLinks(updatedPaymentLinkModelList)
    }

    private suspend fun updatePaymentLinks(paymentLinkList: List<PaymentLinkModel>) = coroutineScope {
        if (paymentLinkList.isEmpty())
            return@coroutineScope

        // Update payment links
        val updatedPaymentLinkList = paymentLinkList
            .mapNotNull { newPaymentLink ->
                val currentPaymentLink = state.value.getPaymentLinks.data
                    .orEmpty()
                    .find { it.id == newPaymentLink.id }
                    ?: return@mapNotNull null

                async {
                    val changedFields = currentPaymentLink.getChangedFields(
                        paymentLinkModel = newPaymentLink,
                    )

                    paymentLinkRepository.update(changedFields)
                }
            }
            .awaitAll()
            .mapNotNull { it.getOrNull() }

        val newPaymentLinkModelList =
            state.value.getPaymentLinks.data
                ?.map { paymentLink ->
                    updatedPaymentLinkList.find { it.id == paymentLink.id } ?: paymentLink
                }

        state.update {
            it.copy(
                getPaymentLinks = it.getPaymentLinks.copy(
                    data = newPaymentLinkModelList,
                ),
            )
        }
    }

    private fun sparseUpdateInvoice(event: AddEditInvoiceEvent.SparseUpdateInvoice) =
        getData(
            getData = { quickbooksClient.sparseUpdateInvoice(event.quickbookSparseRequest) },
            getState = { state.value.sparseUpdateInvoice },
            setState = { newState -> state.update { it.copy(sparseUpdateInvoice = newState) } },
            onSuccess = { qbInvoice ->
                state.update {
                    it.copy(
                        getQuickbooksInvoice = State.success(qbInvoice),
                        sparseUpdateInvoice = State(),
                    )
                }
            },
        )

    private fun downloadInvoice(event: AddEditInvoiceEvent.DownloadInvoice) {
        scope.launch {
            state.update {
                it.copy(
                    loadingDownloadQuickbookIds = it.loadingDownloadQuickbookIds + event.qb.Id,
                )
            }

            getDataSuspend(
                getData = {
                    quickbooksClient
                        .downloadInvoicePdf(event.qb.Id)
                        .map {
                            it to event.qb
                        }
                },
                getState = { state.value.downloadQuickbooksInvoice },
                setState = { newState -> state.update { it.copy(downloadQuickbooksInvoice = newState) } },
            )

            state.update {
                it.copy(
                    loadingDownloadQuickbookIds = it.loadingDownloadQuickbookIds - event.qb.Id,
                )
            }
        }
    }

    private fun clearDownloadInvoice() {
        state.update {
            it.copy(
                downloadQuickbooksInvoice = State(),
            )
        }
    }

    private fun getClientTravelAgent(): Either<ClientModel, TravelAgentModel> {
        return if (state.value.clientInvoice) {
            val client =
                state.value.getProposal.data?.primaryClient ?: state.value.getItinerary.data?.proposal?.primaryClient
            require(client != null) { "Client can't be null" }
            Either.Left(client)
        } else {
            val travelAgent =
                state.value.getProposal.data?.travelAgent ?: state.value.getItinerary.data?.proposal?.travelAgent
            require(travelAgent != null) { "Travel Agent can't be null" }
            Either.Right(travelAgent)
        }
    }

    private fun createUpdateInvoice() {
        scope.launch {
            state.update {
                it.copy(
                    addEditInvoice = State.loading(),
                )
            }

            loadingManager.emitLoading(true)

            val proposal = state.value.getProposal.data
            val itinerary = state.value.getItinerary.data

            if (itinerary == null) {
                require(proposal != null) { "Proposal can't be null" }
                if (state.value.getInvoice.data == null) {
                    if (!createInvoice(proposal)) return@launch
                } else
                    if (!updateInvoice()) return@launch
            } else {
                if (!updateInvoice(itinerary)) return@launch
            }
        }.invokeOnCompletion {
            scope.launch {
                loadingManager.emitLoading(false)
                if (state.value.addEditInvoice.isSuccess)
                    state.value.addEditInvoice.data?.let {
                        clearSuccessState(it)
                    }
                else {
                    state.update {
                        it.copy(
                            addEditInvoice = State.failure(),
                        )
                    }

                    if (it != null) {
                        toastManager.error(description = "Failed to create invoice: ${it.message}")
                        captureException(it)
                    }
                }
            }
        }
    }

    private suspend fun createInvoice(proposal: ProposalModel): Boolean {
        val customerId = getCustomerId()

        val customerRequest = getCustomerRequest()

        val customField = getCustomField(proposal)

        val customerRef = getOrCreateCustomer(customerId, customerRequest) ?: return false

        println("Get or create customer success")

        val proposalServiceIds = getServiceIdsToPay()

        println("Get proposal service ids success")

        val lineList = getLineList(serviceIds = proposalServiceIds) ?: return false

        require(lineList.isNotEmpty()) { "Line list cannot be empty" }

        println("Get line list success")

        val docNumber = getDocNumber() ?: "0000"

        println("Get DocNumber success $docNumber")

        val invoiceRequest = QuickbooksInvoiceRequest(
            docNumber = docNumber.toIntOrNull()?.let {
                (it + 1).toString()
            } ?: "0000",
            dueDate = formatDateToYYYYMMDD(fromDDTOMM(state.value.dueDate)),
            line = lineList,
            customField = customField,
            customerRef = QuickbooksInvoiceRequest.CustomerRef(
                value = customerRef,
            ),
            customerMemo =
                QuickbooksInvoiceRequest.CustomerMemo(
                    value = state.value.notesToCustomer.value,
                )
                    .takeIf {
                        it.value.isNotEmpty()
                    },
        )

        val quickbooksInvoice = quickbooksClient
            .insertInvoice(invoiceRequest)
            .catchQuickbooksUnauthorizedException()
            .getOrNull() ?: return false

        println("Insert invoice success")

        val invoiceModel = createDatabaseInvoice(
            proposalId = proposal.id,
            paymentLinkIds = emptyList(),
            proposalServiceIds = proposalServiceIds,
            extraServiceIds = state.value.extraServiceList.data
                .orEmpty()
                .map { it.id },
            quickbooksInvoiceId = quickbooksInvoice.Id,
        ) ?: return false

        println("Create database invoice success")

        state.update {
            it.copy(
                addEditInvoice = State.success(invoiceModel),
            )
        }

        return true
    }

    private suspend fun updateInvoice(itinerary: ItineraryModel? = null): Boolean {
        val serviceIds = getServiceIdsToPay(itinerary)

        val lineList = getLineList(
            serviceIds = serviceIds,
            itinerary = itinerary,
        ) ?: return false

        println("Get line list success")

        require(lineList.isNotEmpty()) { "Line list cannot be empty" }

        val updateInvoiceRequest = QuickbookUpdateRequest(
            Id = state.value.getQuickbooksInvoice.data?.Id.orEmpty(),
            SyncToken = state.value.getQuickbooksInvoice.data?.SyncToken.orEmpty(),
            Line = lineList,
            sparse = true,
        )

        val quickbooksInvoice = quickbooksClient
            .updateInvoice(updateInvoiceRequest)
            .getOrNull() ?: return false

        val invoiceModel = updateDatabaseInvoice(
            serviceIds = serviceIds,
            extraServiceIds = state.value.extraServiceList.data
                .orEmpty()
                .map { it.id },
            quickbooksInvoiceId = quickbooksInvoice.Id,
            itinerary = itinerary,
        ) ?: return false

        println("Update database invoice success")

        state.update {
            it.copy(
                addEditInvoice = State.success(invoiceModel),
                getInvoice = state.value.getInvoice.copy(data = invoiceModel),
                getQuickbooksInvoice = state.value.getQuickbooksInvoice.copy(data = quickbooksInvoice),
            )
        }

        return true
    }

    private suspend fun createDatabaseInvoice(
        proposalId: String,
        paymentLinkIds: List<String>,
        proposalServiceIds: List<String>,
        extraServiceIds: List<String>,
        quickbooksInvoiceId: String,
    ): InvoiceModel? {
        val invoice =
            InvoiceModel.empty.copy(
                paymentLinkIds = paymentLinkIds,
                proposalServiceIds = proposalServiceIds,
                extraServiceIds = extraServiceIds,
                qbId = quickbooksInvoiceId,
                dueDate = state.value.dueDate,
                proposalId = proposalId,
            )

        return invoiceRepository
            .insert(invoice)
            .getOrNull()
    }

    private suspend fun updateDatabaseInvoice(
        serviceIds: List<String>,
        extraServiceIds: List<String>,
        quickbooksInvoiceId: String,
        itinerary: ItineraryModel? = null,
    ): InvoiceModel? {
        return state.value.getInvoice.data?.let {
            invoiceRepository
                .update(
                    it.copy(
                        proposalServiceIds = if (itinerary == null)
                            serviceIds else
                            it.proposalServiceIds,
                        itineraryServiceIds = if (itinerary != null)
                            serviceIds else
                            it.itineraryServiceIds,
                        extraServiceIds = extraServiceIds,
                        qbId = quickbooksInvoiceId,
                    ),
                )
                .getOrNull()
        }
    }

    private fun clearSuccessState(invoice: InvoiceModel) =
        scope.launch {
            val getInvoice = state.value.getInvoice.data ?: run {
                getInvoice(AddEditInvoiceEvent.GetInvoice(invoiceId = invoice.id))
                return@launch
            }

            val itineraryId = invoice.itineraryId

            val serviceJob = launch {
                if (!itineraryId.isNullOrEmpty())
                    getInvoice.itineraryServiceIds?.let { ids ->
                        val data = if (ids.isNotEmpty()) {
                            itineraryRepository.getItineraryServicesByIds(ids = ids).getOrElse { emptyList() }
                        } else {
                            emptyList()
                        }
                        state.update {
                            it.copy(
                                getInvoiceItineraryServices = state.value.getInvoiceItineraryServices.copy(
                                    data = data,
                                ),
                            )
                        }
                    }
                else
                    getInvoice.proposalServiceIds?.let { ids ->
                        val data = if (ids.isNotEmpty()) {
                            proposalRepository.getAllProposalServiceByIds(ids = ids).getOrElse { emptyList() }
                        } else {
                            emptyList()
                        }
                        state.update {
                            it.copy(
                                getInvoiceProposalServices = state.value.getInvoiceProposalServices.copy(
                                    data = data,
                                ),
                            )
                        }
                    }
            }

            val extraServiceJob = launch {
                getInvoice.extraServiceIds?.let { ids ->
                    val data = if (ids.isNotEmpty()) {
                        serviceRepository.getExtraServicesByIds(ids = ids).getOrElse { emptyList() }
                    } else {
                        emptyList()
                    }
                    state.update { it.copy(extraServiceList = state.value.extraServiceList.copy(data = data)) }
                }
            }

            joinAll(serviceJob, extraServiceJob)

        }.invokeOnCompletion {
            state.update { it.copy(addEditInvoice = State()) }
        }

    private fun getCustomerId(): String? =
        when (val cTa = getClientTravelAgent()) {
            is Either.Left -> cTa.value.qbCustomerId
            is Either.Right -> cTa.value.qbCustomerId
        }

    private fun getCustomerRequest(): QuickbooksCustomerRequest {
        return when (val cTa = getClientTravelAgent()) {
            is Either.Left -> QuickbooksCustomerRequest(
                GivenName = cTa.value.firstName ?: "",
                FamilyName = cTa.value.lastName,
                DisplayName = cTa.value.prettyName,
                PrimaryEmailAddr = cTa.value.email?.let {
                    PrimaryEmailAddr(
                        Address = it,
                    )
                },
            )

            is Either.Right -> QuickbooksCustomerRequest(
                GivenName = cTa.value.contact?.firstName.orEmpty(),
                FamilyName = cTa.value.contact?.lastName.orEmpty(),
                DisplayName = (cTa.value.prettyName) + cTa.value.contact?.organization?.name?.let { " ($it)" },
                PrimaryEmailAddr = cTa.value.contact?.email?.let {
                    PrimaryEmailAddr(
                        Address = it,
                    )
                },
                PrimaryPhone = cTa.value.contact?.phoneNumber?.let {
                    PrimaryPhone(
                        FreeFormNumber = it,
                    )
                },
                CompanyName = cTa.value.contact?.organization?.name,
                BillAddr = CustomerBillAddr(
                    City = cTa.value.contact?.address?.city,
                    PostalCode = cTa.value.contact?.address?.zipCode,
                    Line1 = cTa.value.contact?.address?.line1,
                    Country = cTa.value.contact?.address?.country,
                ),
            )
        }
    }

    private fun getCustomField(proposal: ProposalModel): List<QuickbooksInvoiceRequest.CustomField> {
        val proposalShareCodeField = QuickbooksInvoiceRequest.CustomField(
            DefinitionId = "1",
            Name = "Proposal",
            Type = "StringType",
            StringValue = proposal.shareCode.orEmpty(),
        )

        val customerField = QuickbooksInvoiceRequest.CustomField(
            DefinitionId = "2",
            Name = "Client",
            Type = "StringType",
            StringValue = state.value.getProposal.data?.primaryClient?.prettyName.orEmpty(),
        )

        return listOf(proposalShareCodeField, customerField)
    }

    private suspend fun getOrCreateCustomer(
        customerId: String?,
        customerRequest: QuickbooksCustomerRequest,
    ): String? {

        // Try to get the customer from Quickbooks
        val getCustomerResult =
            if (customerId != null)
                quickbooksClient
                    .getCustomer(customerId)
                    .catchQuickbooksUnauthorizedException()
                    .getOrNull()
            else
                null

        // If the customer does not exist, insert it
        val insertCustomerResult =
            if (getCustomerResult == null) {
                quickbooksClient
                    .getOrInsertCustomer(customerRequest)
                    .catchQuickbooksUnauthorizedException()
                    .onSuccess {
                        when (val cTa = getClientTravelAgent()) {
                            is Either.Left -> clientRepository.update(cTa.value.copy(qbCustomerId = it.Id))
                            is Either.Right -> travelAgentRepository.update(cTa.value.copy(qbCustomerId = it.Id))
                        }
                    }
                    .map {
                        it.Id
                    }
            } else
                Result.success(getCustomerResult.Id)

        return insertCustomerResult.getOrNull()
    }

    private fun getServiceIdsToPay(itinerary: ItineraryModel? = null): List<String> {
        val proposalServiceList = state.value.getAllProposalServices
        val itineraryServiceList = state.value.getAllItineraryServices
        val checkedServiceIds = state.value.checkedServiceIds

        if (itinerary != null)
            return itineraryServiceList
                .filter {
                    it.id in checkedServiceIds &&
                        it.status != ItineraryServiceModel.Status.Cancelled
                }
                .map { itineraryService -> itineraryService.id }
                .distinct()
        else
            return proposalServiceList
                .filter {
                    it.id in checkedServiceIds &&
                        it.paid != true &&
                        it.archived != true
                }
                .map { proposalService -> proposalService.id }
                .distinct()
    }

    private suspend fun getLineList(
        serviceIds: List<String>,
        itinerary: ItineraryModel? = null,
    ): List<QuickbooksInvoiceRequest.Line>? =
        coroutineScope {
            val lineList = mutableListOf<QuickbooksInvoiceRequest.Line>()

            val serviceDeferredList =
                getOrCreateServicesLine(
                    serviceIds = serviceIds,
                    itinerary = itinerary,
                ) ?: return@coroutineScope null

            val extraServiceDeferredList =
                getOrCreateExtraServicesLine() ?: return@coroutineScope null

            serviceDeferredList
                .onEachSafeAwait { line ->
                    lineList.add(line)
                }
                .let { isSuccess ->
                    if (!isSuccess)
                        return@coroutineScope null
                }

            extraServiceDeferredList
                .onEachSafeAwait { line ->
                    lineList.add(line)
                }
                .let { isSuccess ->
                    if (!isSuccess)
                        return@coroutineScope null
                }

            return@coroutineScope lineList
        }

    private suspend fun getDocNumber(): String? =
        quickbooksClient
            .getLatestInvoiceDocNumber()
            .catchQuickbooksUnauthorizedException()
            .getOrNull()

    private suspend fun getOrCreateServicesLine(
        serviceIds: List<String>,
        itinerary: ItineraryModel? = null,
    ): List<Deferred<Result<QuickbooksInvoiceRequest.Line>>>? {
        val proposalServiceList = state.value.getAllProposalServices
            .filter { it.id in serviceIds }
            .sortedWith(
                compareBy(
                    { it.startDate },
                    { it.index },
                ),
            )

        val itineraryServiceList = state.value.getAllItineraryServices
            .filter { it.id in serviceIds }
            .sortedWith(
                compareBy { it.startDate },
            )

        return if (itinerary != null)
            servicesInBatches(
                serviceItems = itineraryServiceList,
                getService = { it.service },
                getPrice = { it.price },
                getQuantity = { 1 },
                getDate = { it.startDate },
                copyItem = { itineraryService, service -> itineraryService.copy(service = service) },
                onSuccess = { returnedService ->
                    state.value = state.value.copy(
                        getItineraryServices = State.success(
                            data = updateServices(
                                items = state.value.getItineraryServices.data.orEmpty(),
                                getServiceId = { it.service.id },
                                copyItem = { itineraryService -> itineraryService.copy(service = returnedService) },
                                service = returnedService,
                            ),
                        ),
                        getInvoiceItineraryServices = State.success(
                            data = updateServices(
                                items = state.value.getInvoiceItineraryServices.data.orEmpty(),
                                getServiceId = { it.service.id },
                                copyItem = { itineraryService -> itineraryService.copy(service = returnedService) },
                                service = returnedService,
                            ),
                        ),
                    )
                },
            )
        else
            servicesInBatches(
                serviceItems = proposalServiceList,
                getService = { it.service },
                getPrice = { it.price },
                getQuantity = { 1 },
                getDate = { it.startDate },
                copyItem = { proposalService, service -> proposalService.copy(service = service) },
                onSuccess = { returnedService ->
                    state.value = state.value.copy(
                        getProposalServices = State.success(
                            data = updateServices(
                                items = state.value.getProposalServices.data.orEmpty(),
                                getServiceId = { it.service.id },
                                copyItem = { proposalService -> proposalService.copy(service = returnedService) },
                                service = returnedService,
                            ),
                        ),
                        getInvoiceProposalServices = State.success(
                            data = updateServices(
                                items = state.value.getInvoiceProposalServices.data.orEmpty(),
                                getServiceId = { it.service.id },
                                copyItem = { proposalService -> proposalService.copy(service = returnedService) },
                                service = returnedService,
                            ),
                        ),
                    )
                },
            )
    }

    private suspend fun getOrCreateCategoryList(services: List<ServiceModel>): List<String> = coroutineScope {
        val itemList = mutableListOf<String>()

        state.value.getAllCategory.data.orEmpty()
            .filter { c -> services.any { s -> s.category == c.id } }
            .forEach { category ->
                val qbCategoryId = category.qbCategoryId ?: run {
                    val result = insertQuickbooksCategory(category.name)
                    result.onSuccess { quickBooksItem ->
                        categoryRepository.update(category.copy(qbCategoryId = quickBooksItem.Id))
                    }.getOrNull()
                    result.getOrNull()?.Id
                }
                qbCategoryId?.let { itemList.add(it) }
            }

        itemList
    }

    private suspend fun getOrCreateExtraServicesLine(): List<Deferred<Result<QuickbooksInvoiceRequest.Line>>>? =
        servicesInBatches(
            serviceItems = state.value.extraServiceList.data.orEmpty(),
            getService = { it.service },
            getPrice = { it.unitPrice },
            getQuantity = { it.quantity ?: 1 },
            getDate = { it.createdAt },
            copyItem = { extraService, service -> extraService.copy(service = service) },
            onSuccess = { returnedService ->
                state.value = state.value.copy(
                    extraServiceList = State.success(
                        data = updateServices(
                            items = state.value.extraServiceList.data.orEmpty(),
                            getServiceId = { it.service.id },
                            copyItem = { extraService -> extraService.copy(service = returnedService) },
                            service = returnedService,
                        ),
                    ),
                )
            },
        )

    private suspend fun <T> servicesInBatches(
        serviceItems: List<T>,
        getService: (T) -> ServiceModel,
        getPrice: (T) -> Int?,
        getQuantity: (T) -> Int,
        getDate: (T) -> String,
        copyItem: (T, ServiceModel) -> T,
        onSuccess: (ServiceModel) -> Unit,
    ): List<Deferred<Result<QuickbooksInvoiceRequest.Line>>>? {
        return coroutineScope {
            val items = serviceItems.toMutableList()

            val services = items.map { getService(it) }

            val categoryList = getOrCreateCategoryList(services)

            val itemList = mutableListOf<QuickbooksItem?>()

            // Process services in batches of 9
            for (i in items.indices step 9) {
                val batch = items.subList(i, minOf(i + 9, items.size))

                val batchDeferredItems = batch.mapNotNull { item ->
                    val service = getService(item)
                    service.let {
                        if (service.qbItemId != null) {
                            // Directly use the existing qbItemId
                            itemList.add(QuickbooksItem(Id = service.qbItemId!!))
                            null // No async operation needed for this service
                        } else {
                            val category = state.value.getAllCategory.data.orEmpty().find { it.id == service.category }

                            async {
                                insertQuickBooksItem(
                                    name = service.title,
                                    parentRef = categoryList
                                        .find { it == category?.qbCategoryId }
                                        ?.let {
                                            QuickbooksItemRequest.AccountRef(
                                                value = it,
                                            )
                                        },
                                ).onSuccess { quickBooksItem ->
                                    serviceRepository
                                        .update(service.copy(qbItemId = quickBooksItem.Id))
                                        .onSuccess { returnedService ->
                                            val index = items.indexOfFirst { getService(it).id == returnedService.id }
                                            if (index != -1)
                                                items[index] = copyItem(item, returnedService)

                                            onSuccess(returnedService)
                                        }
                                }
                            }
                        }
                    }
                }

                // Wait for the current batch to complete
                batchDeferredItems
                    .onEachSafeAwait { item ->
                        itemList.add(item)
                    }
                    .let { isSuccess ->
                        if (!isSuccess)
                            return@coroutineScope null
                    }

                // Delay between batches
                if (i + 9 < items.size) {
                    delay(500L) // 0.5-second delay
                }
            }

            val lineList = items.mapNotNull { item ->
                val service = getService(item)

                val qbItem = itemList.find {
                    it?.Id == service.qbItemId
                }.also { qbItem ->
                    println("QBItem Creation: $qbItem")
                } ?: return@mapNotNull null

                val price = (getPrice(item) ?: 0) / 100.0
                val serviceDate = getDate(item)

                async {
                    Result.success(
                        QuickbooksInvoiceRequest.Line(
                            DetailType = SALES_ITEM_LINE_DETAIL,
                            Amount = price * getQuantity(item),
                            SalesItemLineDetail = QuickbooksInvoiceRequest.SalesItemLineDetail(
                                ItemRef = QuickbooksInvoiceRequest.ItemRef(
                                    value = qbItem.Id,
                                ),
                                UnitPrice = price,
                                Qty = getQuantity(item),
                                ServiceDate = formatDateToYYYYMMDD(serviceDate),
                            ),
                            Description = service.headline,
                        ),
                    )
                }
            }

            return@coroutineScope lineList
        }
    }

    private fun <T> updateServices(
        items: List<T>?,
        copyItem: (T) -> T,
        getServiceId: (T) -> String,
        service: ServiceModel,
    ): List<T> =
        items
            .orEmpty()
            .map { item ->
                if (getServiceId(item) == service.id)
                    copyItem(item) else
                    item
            }

    private suspend fun insertQuickBooksItem(
        name: String,
        parentRef: QuickbooksItemRequest.AccountRef? = null,
    ): Result<QuickbooksItem> =
        quickbooksClient
            .insertItem(
                QuickbooksItemRequest(
                    subItem = parentRef != null,
                    Name = name,
                    IncomeAccountRef = QuickbooksItemRequest.AccountRef(
                        name = "Sales of Product Income",
                        value = EssntlConfig.quickbooksIncomeAccountId,
                    ),
                    ParentRef = parentRef,
                ),
            )
            .catchQuickbooksUnauthorizedException()

    private suspend fun insertQuickbooksCategory(name: String): Result<QuickbooksCategory> =
        quickbooksClient
            .insertCategory(
                QuickbooksCategoryRequest(
                    name = name,
                    type = "Category",
                ),
            )
            .catchQuickbooksUnauthorizedException()

    private fun <T> Result<T>.catchQuickbooksUnauthorizedException(): Result<T> =
        onFailure { e ->
            if (e is BadRequestException && e.code == 401 && !state.value.openQuickbooksRefreshWebpage) {
                state.update {
                    it.copy(
                        openQuickbooksRefreshWebpage = true,
                    )
                }
            }
        }
}